/**
 * Lightweight logger — silences console output in production and
 * POSTs `error()` calls to /api/errors/client for centralised triage.
 *
 * Drop-in replacement for `console`:
 *     import logger from '../lib/logger';
 *     logger.log('hello');
 *     logger.warn('something odd');
 *     logger.error(err, { extra: 'context' });
 *
 * Behaviour:
 *   - .log / .info / .debug — silent in production, emit in dev.
 *   - .warn — emits in both.
 *   - .error — emits to console AND POSTs to /api/errors/client
 *             (fire-and-forget, no auth required, rate-limited server-side,
 *             de-duped by stack hash over a 60-minute window).
 */
const isProd = process.env.NODE_ENV === 'production';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Simple client-side throttle — don't post more than 1 error / 500 ms.
let _lastReportAt = 0;
const MIN_GAP_MS = 500;

function _extractError(args) {
  // Walks the arguments to pull out the primary Error instance (if any)
  // plus any accompanying metadata for the server's `context` field.
  let message = '';
  let stack = '';
  const context = {};
  for (const a of args) {
    if (a instanceof Error) {
      message = message || a.message || a.toString();
      stack = stack || a.stack || '';
    } else if (typeof a === 'string') {
      message = message ? `${message} ${a}` : a;
    } else if (a && typeof a === 'object') {
      try {
        Object.assign(context, a);
      } catch {
        /* ignore */
      }
    }
  }
  if (!message && args.length) {
    try {
      message = String(args[0]);
    } catch {
      message = '(unserialisable)';
    }
  }
  return { message: message || '(empty)', stack, context };
}

function _reportToServer(args) {
  if (!API_URL) return;
  const now = Date.now();
  if (now - _lastReportAt < MIN_GAP_MS) return;
  _lastReportAt = now;

  const { message, stack, context } = _extractError(args);
  const payload = {
    message: String(message).slice(0, 500),
    stack: stack ? String(stack).slice(0, 8000) : '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    context,
  };

  try {
    // Prefer sendBeacon — survives page unload, doesn't block the thread.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const ok = navigator.sendBeacon(`${API_URL}/api/errors/client`, blob);
      if (ok) return;
    }
    // Fallback — fire-and-forget fetch. keepalive so we don't block nav.
    fetch(`${API_URL}/api/errors/client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {
      /* swallow — we never want the reporter itself to throw */
    });
  } catch {
    /* swallow */
  }
}

const noop = () => {};

const logger = {
  log: isProd ? noop : (...args) => console.log(...args),          // eslint-disable-line no-console
  info: isProd ? noop : (...args) => console.info(...args),        // eslint-disable-line no-console
  debug: isProd ? noop : (...args) => console.debug(...args),      // eslint-disable-line no-console
  warn: (...args) => console.warn(...args),                        // eslint-disable-line no-console
  error: (...args) => {
    console.error(...args);                                        // eslint-disable-line no-console
    // Only phone home in production builds — local dev noise isn't useful
    // (and can flood the DB when HMR throws transient errors).
    if (isProd) {
      _reportToServer(args);
    }
  },
};

// Opt-in hook for callers that WANT to report in dev too (e.g. manual
// fire-and-forget tracking). Rarely needed.
logger.reportError = (...args) => _reportToServer(args);

export default logger;
